import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as wishlistService from "../services/wishlistService";
import toast from "react-hot-toast";
import getOptimizedUrl from '../utils/imageUtils';
import { isAccessory, isBottomProductType } from '../utils/productUtils';
import {
  CUSTOM_LENGTH_RANGE,
  CUSTOM_WAIST_RANGE,
  DEFAULT_CUSTOM_LENGTH,
  DEFAULT_CUSTOM_WAIST,
  formatCustomSizeLabel,
  parseCustomMeasurements,
} from "../utils/customSizeUtils";

const CUSTOM_LENGTH_CHART = {
  height: ["5'' - 5'2", "5'3 - 5'4", "5'5 - 5'6", "5'7 - 5'8", "5'9 - 5'10", "5'10 - 6''"],
  recommended: [39, 40, 40, 41, 42, 43],
};

export default function WishlistSidebar() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [closing, setClosing] = useState(false);
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customModalOpen, setCustomModalOpen] = useState(false);
  const [customModalItemKey, setCustomModalItemKey] = useState(null);
  const [customLength, setCustomLength] = useState(DEFAULT_CUSTOM_LENGTH);
  const [customWaist, setCustomWaist] = useState(DEFAULT_CUSTOM_WAIST);
  const [pendingCustomByItem, setPendingCustomByItem] = useState({});
  const [addingToCartByItem, setAddingToCartByItem] = useState({});
  const [updatingSizeByItem, setUpdatingSizeByItem] = useState({});
  const [removingByItem, setRemovingByItem] = useState({});

  const loadWishlist = async () => {
    try {
      setLoading(true);
      const data = await wishlistService.getWishlist();
      const sorted = Array.isArray(data) ? data.slice().sort((a, b) => new Date(b.addedAt) - new Date(a.addedAt)) : data;
      setWishlist(sorted);
    } catch (error) {
      console.error('Failed to load wishlist:', error);
      if (error.response?.status === 401) {
        // Session expired: keep only signup sidebar visible.
        setClosing(true);
        setIsOpen(false);
        setTimeout(() => {
          setMounted(false);
          setClosing(false);
        }, 300);
        try {
          window.dispatchEvent(new CustomEvent("signup:toggle", { detail: { open: true } }));
        } catch (err) {}
        setWishlist([]);
      }
    } finally {
      setLoading(false);
    }
  };

  // Prevent body scroll when sidebar is open
  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.setProperty('overflow', 'hidden', 'important');
      document.body.style.setProperty('overflow', 'hidden', 'important');
    } else {
      document.documentElement.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow');
    }

    return () => {
      document.documentElement.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow');
    };
  }, [isOpen]);

  useEffect(() => {
    const onToggle = (e) => {
      const open = e?.detail?.open ?? !isOpen;
      if (open) {
        const user = localStorage.getItem('user');
        const token = localStorage.getItem('token');

        if (!user || !token) {
          // Not logged in: do not open wishlist sidebar; open signup only.
          setClosing(true);
          setIsOpen(false);
          setTimeout(() => {
            setMounted(false);
            setClosing(false);
          }, 0);
          try {
            window.dispatchEvent(new CustomEvent("signup:toggle", { detail: { open: true } }));
          } catch (err) {}
          return;
        }

        setMounted(true);
        setClosing(false);
        setIsOpen(true);
        loadWishlist();
      } else {
        setClosing(true);
        setIsOpen(false);
        setTimeout(() => {
          setMounted(false);
          setClosing(false);
        }, 300);
      }
    };

    window.addEventListener("wishlist:toggle", onToggle);

    return () => {
      window.removeEventListener("wishlist:toggle", onToggle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleAddToCart = async (product, item, e) => {
    e.stopPropagation();

    const itemKey = item?._id || product?._id || item?.productId;
    if (addingToCartByItem[itemKey]) return;

    const pendingCustomSelection = pendingCustomByItem[itemKey];
    const hasPendingCustom = Number.isFinite(pendingCustomSelection?.length);
    const customSizeValue = hasPendingCustom
      ? formatCustomSizeLabel(
          pendingCustomSelection.length,
          pendingCustomSelection.waist,
        )
      : undefined;

    const user = localStorage.getItem("user");
    const token = localStorage.getItem("token");
    if (!user || !token) {
      try {
        window.dispatchEvent(new CustomEvent("signup:toggle", { detail: { open: true } }));
      } catch (err) {}
      toast.error('Please login to add items to cart');
      return;
    }

    // If wishlist entry has no size, ask user to select one first (UNLESS it's an accessory)
    if (!item?.size && !isAccessory(product?.productType) && !hasPendingCustom) {
      toast.error('Please select a size before adding to cart');
      return;
    }

    // Auto-set freesize for accessories
    const effectiveSize = isAccessory(product?.productType)
      ? 'Freesize'
      : hasPendingCustom
        ? 'Custom'
        : item.size;

    try {
      setAddingToCartByItem((prev) => ({ ...prev, [itemKey]: true }));

      // fetch current server cart
      let serverCart = [];
      try {
        serverCart = await (await import('../services/cartService')).getCart();
      } catch (err) {
        console.error('Failed to fetch server cart', err);
        serverCart = [];
      }

      // build next cart payload
      const mappedLocal = Array.isArray(serverCart)
        ? serverCart.map(i => ({
            productId: i.product._id,
            size: i.size || '',
            quantity: i.qty ?? i.quantity ?? 1,
            customSize: i.customSize,
          }))
        : [];
      const existingIndex = mappedLocal.findIndex(
        c =>
          String(c.productId) === String(product._id) &&
          c.size === effectiveSize &&
          String(c.customSize || '') === String(customSizeValue || ''),
      );
      if (existingIndex !== -1) {
        mappedLocal[existingIndex].quantity += 1;
      } else {
        mappedLocal.push({
          productId: product._id,
          size: effectiveSize,
          quantity: 1,
          customSize: customSizeValue,
        });
      }

      // send to server
      const payload = mappedLocal.map(i => ({
        product: i.productId,
        qty: i.quantity,
        size: i.size,
        customSize: i.customSize,
      }));
      const updateCart = (await import('../services/cartService')).updateCart;
      await updateCart(payload);
      window.dispatchEvent(new Event('cart:updated'));

      // Remove this item from wishlist after successful cart add.
      const removeSize = item?.size;
      if (removeSize) {
        await wishlistService.removeFromWishlist(product._id, removeSize);
      } else {
        await wishlistService.removeFromWishlist(product._id);
      }

      const refreshedWishlist = await wishlistService.getWishlist();
      const sortedRefreshed = Array.isArray(refreshedWishlist)
        ? refreshedWishlist.slice().sort((a, b) => new Date(b.addedAt) - new Date(a.addedAt))
        : [];
      setWishlist(sortedRefreshed);
      window.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { wishlist: sortedRefreshed } }));

      toast.success('Added to cart!');
      if (hasPendingCustom) {
        setPendingCustomByItem((prev) => {
          const next = { ...prev };
          delete next[itemKey];
          return next;
        });
      }
    } catch (err) {
      console.error('Failed to add wishlist item to cart', err);
      const backendMsg = err?.response?.data?.message;
      if (!backendMsg) toast.error('Failed to add to cart');
    } finally {
      setAddingToCartByItem((prev) => {
        const next = { ...prev };
        delete next[itemKey];
        return next;
      });
    }
  };

  const handleRemove = async (productId, e) => {
    e.stopPropagation();
    if (removingByItem[productId]) return;
    try {
      setRemovingByItem((prev) => ({ ...prev, [productId]: true }));
      // Find wishlist item to determine if it had a size
      const item = wishlist.find(it => (it.product?._id || it.productId || it._id) === productId);
      const size = item?.size;
      await wishlistService.removeFromWishlist(productId, size);
      const updatedWishlist = wishlist.filter(it => (it.product?._id || it.productId || it._id) !== productId);
      const sorted = updatedWishlist.slice().sort((a, b) => new Date(b.addedAt) - new Date(a.addedAt));
      setWishlist(sorted);
      toast.success('Removed from wishlist');
      window.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { wishlist: sorted } }));
    } catch (error) {
      console.error('Failed to remove from wishlist:', error);
      toast.error('Failed to remove from wishlist');
    } finally {
      setRemovingByItem((prev) => {
        const next = { ...prev };
        delete next[productId];
        return next;
      });
    }
  };
  
  const updateSizeWishlist = async (productId, newSize, itemKey) => {
    if (updatingSizeByItem[itemKey]) return;

    // Find existing item to know old size
    const existing = wishlist.find(it => (it.product?._id || it.productId || it._id) === productId);
    const oldSize = existing?.size;
    if (oldSize === newSize) return; // nothing to do

    try {
      setUpdatingSizeByItem((prev) => ({ ...prev, [itemKey]: true }));

      // If there was an old size saved on server, remove that specific entry first
      if (oldSize) {
        await wishlistService.removeFromWishlist(productId, oldSize);
      } else {
        // Remove any product-only entry (server will handle product-only removal)
        await wishlistService.removeFromWishlist(productId);
      }

      // Add new wishlist entry with selected size
      await wishlistService.addToWishlist(productId, newSize);

      // Refresh wishlist from server to ensure canonical state (newest first)
      const data = await wishlistService.getWishlist();
      const sorted = Array.isArray(data) ? data.slice().sort((a, b) => new Date(b.addedAt) - new Date(a.addedAt)) : (data || []);
      setWishlist(sorted);
      toast.success('Size updated');
      window.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { wishlist: sorted } }));
    } catch (err) {
      console.error('Failed to update wishlist size', err);
      toast.error('Failed to update size');
    } finally {
      setUpdatingSizeByItem((prev) => {
        const next = { ...prev };
        delete next[itemKey];
        return next;
      });
    }
  };



  const closeSidebar = () => {
    setClosing(true);
    setIsOpen(false);
    // Custom selection in wishlist is temporary unless user adds to cart.
    setPendingCustomByItem({});
    setCustomModalOpen(false);
    setCustomModalItemKey(null);
    setCustomLength(DEFAULT_CUSTOM_LENGTH);
    setCustomWaist(DEFAULT_CUSTOM_WAIST);
    setTimeout(() => setMounted(false), 300);
  };

  return (
    <>
      {mounted && (
        <>
          <div
            className="fixed inset-0 bg-black/40 font-bdogrotesk z-[10000]"
            onClick={closeSidebar}
          />

          <div
            className={`fixed top-0 right-0 bottom-0 w-full max-w-lg bg-white z-[10001] shadow-2xl flex flex-col drawer-panel ${
              closing ? "drawer-slide-out" : "drawer-slide-in"
            }`}
          >
            {/* Header */}
            <div className="flex justify-between items-center p-2 ">
              <h2 className="text-base font-medium  text-gray-900">Wishlist</h2>
              <button
                onClick={closeSidebar}
                className="text-base font-medium text-black font-bdogrotesk"
              >
                Close
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 no-scrollbar ">
              {loading ? (
                <div className="space-y-4">
                  {[1,2,3].map((i) => (
                    <div key={i} className="flex gap-4 p-4 animate-pulse">
                      <div className="w-24 h-24 bg-gray-200 rounded-md" />
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-3" />
                        <div className="h-3 bg-gray-200 rounded w-1/3 mb-4" />
                        <div className="h-8 bg-gray-200 rounded w-32" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : wishlist.length === 0 ? (
                <div className="text-center text-black py-48 font-bold text-2xl">
                  Your wishlist is empty
                </div>
              ) : (
                <div className="space-y-6">
                  {wishlist.map((item) => {
                    const product = item.product || {};
                    const pid = product._id || item.productId || item._id;
                    const itemKey = item._id || pid;
                    const isAddingToCart = Boolean(addingToCartByItem[itemKey]);
                    const isUpdatingSize = Boolean(updatingSizeByItem[itemKey]);
                    const isRemoving = Boolean(removingByItem[pid]);
                    const isBusy = isAddingToCart || isUpdatingSize || isRemoving;
                    const pendingCustomSelection = pendingCustomByItem[itemKey];
                    const hasPendingCustom = Number.isFinite(pendingCustomSelection?.length);
                    const hasServerCustom = String(item.size || '').toLowerCase() === 'custom';
                    const discountPct = product.discountPercent ?? (product.mrp && product.discountedPrice ? Math.round(((product.mrp - product.discountedPrice) / product.mrp) * 100) : 0);
                    return (
                      <div key={item._id} className="relative flex gap-3 sm:gap-4 border-b border-solid py-4 border-b-black items-start">
                        <div
                          className="w-28 h-36 sm:w-40 sm:h-48 bg-white flex-shrink-0 overflow-hidden cursor-pointer"
                          onClick={() => {
                            setClosing(true);
                            setIsOpen(false);
                            setTimeout(() => setMounted(false), 300);
                            navigate(`/product/${product.slug || product._id}`);
                          }}
                        >
                          {product.images && product.images[0] ? (
                            <img
                              src={getOptimizedUrl(product.images[0] || '', 800)}
                              alt={product.product}
                              className="w-full h-full object-cover bg-white"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              No Image
                            </div>
                          )}
                        </div>

                        <div className="flex-1 flex flex-col min-h-[8rem] min-w-0">
                          <div className="font-medium font-bdogrotesk text-sm sm:text-base leading-snug pr-8 sm:pr-10">
                            {product.product}
                          </div>
                          <div className="flex flex-wrap items-center gap-1 text-xs text-gray-500 mt-1.5 sm:mt-2">
                            <div className="text-base sm:text-lg font-bold text-gray-900">
                              ₹{product.discountedPrice || product.mrp}
                            </div>
                            {product.discountedPrice && product.mrp > product.discountedPrice && (
                              <>
                                <span className="text-xs sm:text-sm text-gray-500 line-through">₹{product.mrp}</span>
                                <span className="text-xs font-semibold text-red-500">{discountPct}% OFF</span>
                              </>
                            )}
                          </div>

                          <div className="flex flex-wrap items-center gap-x-1 sm:gap-x-2 gap-y-1 mt-3 sm:mt-4">
                            {isAccessory(product.productType) ? (
                              <div className="text-sm font-semibold text-black">
                                Freesize
                              </div>
                            ) : (
                              <>
                                {['XXS','XS','S','M','L','XL','XXL'].map(sz => (
                                  <button
                                    key={sz}
                                    disabled={isBusy}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPendingCustomByItem((prev) => {
                                        const next = { ...prev };
                                        delete next[itemKey];
                                        return next;
                                      });
                                      updateSizeWishlist(pid, sz, itemKey);
                                    }}
                                    className={`text-xs sm:text-sm px-1 sm:px-2 pb-1.5 sm:pb-3 py-1 ${(item.size === sz && !hasPendingCustom) ? 'font-semibold text-black border-b-2 border-solid border-black' : 'text-gray-400'} ${isBusy ? 'opacity-60 cursor-not-allowed' : ''}`}
                                  >
                                    {sz}
                                  </button>
                                ))}
                                {isBottomProductType(product.productType, product.product) && (
                                  <button
                                    disabled={isBusy}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const initialSelection = hasPendingCustom
                                        ? pendingCustomSelection
                                        : parseCustomMeasurements(item.customSize);
                                      setCustomLength(initialSelection.length);
                                      setCustomWaist(initialSelection.waist);
                                      setCustomModalItemKey(itemKey);
                                      setCustomModalOpen(true);
                                    }}
                                    className={`text-xs sm:text-sm px-1 sm:px-2 pb-1.5 sm:pb-3 py-1 ${
                                      hasPendingCustom || (hasServerCustom && !hasPendingCustom)
                                        ? 'font-semibold text-black border-b-2 border-solid border-black'
                                        : 'text-gray-400'
                                    } ${isBusy ? 'opacity-60 cursor-not-allowed' : ''}`}
                                  >
                                    Custom
                                  </button>
                                )}
                              </>
                            )}
                          </div>

                          <div className="flex items-center gap-3 mt-3 sm:mt-4">
                            <button
                              onClick={(e) => handleAddToCart(product, item, e)}
                              disabled={isBusy}
                              className={`px-3 py-2 bg-white text-black border border-solid text-xs sm:text-sm font-medium ${isBusy ? 'opacity-60 cursor-not-allowed' : ''}`}
                            >
                              {isAddingToCart ? 'Adding...' : 'Add to Cart'}
                            </button>
                            {isUpdatingSize && <span className="text-xs text-gray-500">Updating size...</span>}
                          </div>

                          <button
                            onClick={(e) => { e.stopPropagation(); handleRemove(pid, e); }}
                            disabled={isBusy}
                            className={`absolute top-3 right-3 shrink-0 ${isBusy ? 'opacity-60 cursor-not-allowed' : ''}`}
                            aria-label="Remove from wishlist"
                          >
                            {true ? (
                              <svg
                                width="26"
                                height="26"
                                viewBox="0 0 19 17"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M1.5 3H0.5V8H2V9.5H3.5V11H5V12.5H6.5V14H8V15.5H9V16.5H10V15.5H11V14H12.5V12.5H14V11H15.5V9.5H17V8H18.5V3H17V1.5H15.5V0.5H11.5V2H10V3H8.5V2H7V0.5H3V1.5H1.5V3Z"
                                  fill="#F31717"
                                  stroke="#F31717"
                                />
                              </svg>
                            ) : (
                              <svg
                                width="26"
                                height="26"
                                viewBox="0 0 19 17"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M1.5 3H0.5V8H2V9.5H3.5V11H5V12.5H6.5V14H8V15.5H9V16.5H10V15.5H11V14H12.5V12.5H14V11H15.5V9.5H17V8H18.5V3H17V1.5H15.5V0.5H11.5V2H10V3H8.5V2H7V0.5H3V1.5H1.5V3Z"
                                  fill="white"
                                  stroke="black"
                                />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer: keep Go to Cart pinned at bottom like cart checkout action */}
            {!loading && (
              <div className="p-2">
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => {
                      setClosing(true);
                      setIsOpen(false);
                      setTimeout(() => {
                        setMounted(false);
                        // open cart sidebar after wishlist closes
                        try {
                          window.dispatchEvent(new CustomEvent('cart:toggle', { detail: { open: true } }));
                        } catch (e) {
                          window.dispatchEvent(new Event('cart:updated'));
                        }
                      }, 300);
                    }}
                    className="group w-full bg-black text-white px-3 py-3 flex items-center justify-between text-base font-medium tracking-wide"
                  >
                    <div className="relative h-5 overflow-hidden">
                      <div className="relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
                        <span className="flex font-bdogrotesk h-5 items-center">Go to Cart</span>
                        <span className="flex h-5 items-center">Go to Cart</span>
                      </div>
                    </div>
                  </button>

                  {wishlist.length === 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setClosing(true);
                        setIsOpen(false);
                        setTimeout(() => {
                          setMounted(false);
                          navigate('/store');
                        }, 300);
                      }}
                      style={{
                        border: '1px solid #000',
                        boxSizing: 'border-box',
                        backgroundColor: '#fff',
                      }}
                      className="group w-full text-black bg-white px-3 py-2 flex items-center justify-between text-[15px] font-semibold tracking-wide"
                    >
                      <div className="relative h-5 overflow-hidden flex-1">
                        <div className="relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
                          <span className="flex h-5 text-base items-center">Browse Store</span>
                          <span className="flex h-5 text-base items-center">Browse Store</span>
                        </div>
                      </div>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {customModalOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/60 z-[10002]"
            onClick={() => {
              setCustomModalOpen(false);
              setCustomModalItemKey(null);
              setCustomLength(DEFAULT_CUSTOM_LENGTH);
              setCustomWaist(DEFAULT_CUSTOM_WAIST);
            }}
          />
          <div
            className="fixed inset-0 z-[10003] flex items-center justify-center p-3 sm:p-4"
            onClick={() => {
              setCustomModalOpen(false);
              setCustomModalItemKey(null);
              setCustomLength(DEFAULT_CUSTOM_LENGTH);
              setCustomWaist(DEFAULT_CUSTOM_WAIST);
            }}
          >
            <div
              className="bg-white w-full max-w-[96vw] sm:max-w-4xl md:max-w-5xl max-h-[90vh] overflow-y-auto p-3 sm:p-5 md:p-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-end mb-2">
                <button
                  onClick={() => {
                    setCustomModalOpen(false);
                    setCustomModalItemKey(null);
                    setCustomLength(DEFAULT_CUSTOM_LENGTH);
                    setCustomWaist(DEFAULT_CUSTOM_WAIST);
                  }}
                  className="text-sm font-medium hover:text-gray-600"
                >
                  Close
                </button>
              </div>

              <div className="w-full overflow-x-auto pb-1">
                <table className="w-full min-w-[560px] border-collapse">
                  <tbody>
                    <tr className="border-b border-black/60">
                      <td className="py-2 pr-2 sm:pr-3 text-xs sm:text-sm font-medium">Height</td>
                      {CUSTOM_LENGTH_CHART.height.map((h) => (
                        <td key={h} className="py-2 px-1.5 sm:px-2 text-xs sm:text-sm font-medium text-left whitespace-nowrap">{h}</td>
                      ))}
                    </tr>
                    <tr className="border-b border-black/60">
                      <td className="py-2 pr-2 sm:pr-3 text-xs sm:text-sm font-medium leading-tight">Recommended<br/>Length</td>
                      {CUSTOM_LENGTH_CHART.recommended.map((len, idx) => (
                        <td key={len} className="py-2 px-1.5 sm:px-2 text-xs sm:text-sm font-medium text-left whitespace-nowrap">{idx === CUSTOM_LENGTH_CHART.recommended.length - 1 ? `${len}+` : len}"</td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="mt-4 text-xs text-gray-600 lg:hidden">Select length and waist</p>
              <div className="mt-2 lg:mt-4 flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-4">
                <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto lg:overflow-visible">
                  <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                    <div className="text-xs sm:text-sm font-medium whitespace-nowrap">
                      <span className="lg:hidden">Length</span>
                      <span className="hidden lg:inline">Custom Length</span>
                    </div>
                    <div className="inline-flex items-center border border-black h-9 sm:h-10 w-fit">
                      <button
                        type="button"
                        className="w-7 sm:w-9 h-full text-base sm:text-xl leading-none"
                        onClick={() => setCustomLength((prev) => Math.max(CUSTOM_LENGTH_RANGE.min, prev - 1))}
                      >
                        -
                      </button>
                      <div className="w-12 sm:w-16 text-center text-xs sm:text-sm font-medium">{customLength}"</div>
                      <button
                        type="button"
                        className="w-7 sm:w-9 h-full text-base sm:text-xl leading-none"
                        onClick={() => setCustomLength((prev) => Math.min(CUSTOM_LENGTH_RANGE.max, prev + 1))}
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                    <div className="text-xs sm:text-sm font-medium whitespace-nowrap">
                      <span className="lg:hidden">Waist</span>
                      <span className="hidden lg:inline">Custom Waist</span>
                    </div>
                    <div className="inline-flex items-center border border-black h-9 sm:h-10 w-fit">
                      <button
                        type="button"
                        className="w-7 sm:w-9 h-full text-base sm:text-xl leading-none"
                        onClick={() => setCustomWaist((prev) => Math.max(CUSTOM_WAIST_RANGE.min, prev - 1))}
                      >
                        -
                      </button>
                      <div className="w-12 sm:w-16 text-center text-xs sm:text-sm font-medium">{customWaist}"</div>
                      <button
                        type="button"
                        className="w-7 sm:w-9 h-full text-base sm:text-xl leading-none"
                        onClick={() => setCustomWaist((prev) => Math.min(CUSTOM_WAIST_RANGE.max, prev + 1))}
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (!customModalItemKey) return;
                    setPendingCustomByItem((prev) => ({
                      ...prev,
                      [customModalItemKey]: {
                        length: customLength,
                        waist: customWaist,
                      },
                    }));
                    setCustomModalOpen(false);
                    setCustomModalItemKey(null);
                    setCustomLength(DEFAULT_CUSTOM_LENGTH);
                    setCustomWaist(DEFAULT_CUSTOM_WAIST);
                  }}
                  className="px-2.5 sm:px-4 h-9 sm:h-10 bg-black text-white text-[11px] sm:text-sm font-medium lg:ml-auto whitespace-nowrap w-full lg:w-auto"
                >
                  Order Custom Size
                </button>
              </div>

              <p className="mt-3 text-xs sm:text-sm font-medium leading-snug">
                Customisations are <span className="font-semibold">free of cost</span>! | Customised pieces are not applicable for return/exchange as they're made just for you!
              </p>
            </div>
          </div>
        </>
      )}

      <style jsx>{`
        .drawer-slide-in {
          animation: slideIn 0.3s ease-out forwards;
        }
        .drawer-slide-out {
          animation: slideOut 0.3s ease-in forwards;
        }
        @keyframes slideIn {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        @keyframes slideOut {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(100%);
          }
        }
      `}</style>
    </>
  );
}
